import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AccountActionDialog, type AccountTarget } from "./AccountActionDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ItemPicker } from "@/components/ItemPicker";
import { IapPicker } from "@/components/IapPicker";
import { sendMail, type MailReward } from "@/lib/api";
import { HERO_NAMES, REWARD_TYPE_NAMES } from "@/types/cloud-data";
import { captureMail, createSubmitLock } from "./action-model";
import { IAP_CATALOG, IAP_REWARD_TYPE } from "@/lib/iap-rewards";

const rewardFields: Record<number, { amount?: boolean; item?: boolean; hero?: boolean; buff?: boolean; recipe?: boolean; iap?: boolean }> = {
  0: { amount: true, item: true }, 1: { amount: true }, 2: { amount: true },
  4: { hero: true }, 10: { amount: true, buff: true }, 11: { amount: true },
  12: { amount: true }, 14: { amount: true }, 15: { amount: true }, 16: { recipe: true }, 100: { amount: true, iap: true },
};
const defaultReward: MailReward = { rewardType: 2, amount: 100 };

export function SendMailDialog({ target, onClose, initialRewards, initialSubject }: {
  target: AccountTarget; onClose: () => void; initialRewards?: MailReward[]; initialSubject?: string;
}) {
  const [subject, setSubject] = useState(initialSubject ?? "From the team");
  const [body, setBody] = useState("");
  const [rewards, setRewards] = useState<MailReward[]>(() => initialRewards?.map((reward) => ({ ...reward })) ?? [{ ...defaultReward }]);
  const iapIssues = rewards.flatMap((reward, index) => {
    if (reward.rewardType !== IAP_REWARD_TYPE) return [];
    const product = IAP_CATALOG.find((p) => p.id === reward.iapProductId || p.aliases.includes(reward.iapProductId ?? ""));
    const issue = !product ? "Select a valid IAP product." : product.unavailable;
    return issue ? [`Attachment ${index + 1} (${reward.iapProductId ?? "IAP"}): ${issue}`] : [];
  });
  const [changed, setChanged] = useState(false);
  const [result, setResult] = useState("");
  const submitting = useRef(createSubmitLock());
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: { id: string; name: string | null; rewards: MailReward[]; subject: string; body: string }) =>
      sendMail(payload.id, payload.rewards, payload.subject, payload.body, payload.name),
    onSuccess: (_data, payload) => {
      void qc.invalidateQueries({ queryKey: ["player-mail", payload.id] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
      setResult("Mail saved to this account's backend inbox. Legacy-client delivery is not confirmed.");
      setBody(""); setRewards([{ ...defaultReward }]); setChanged(false);
    },
    onSettled: () => { submitting.current.end(); },
  });

  function update(i: number, patch: Partial<MailReward>) {
    setChanged(true); setResult("");
    setRewards((current) => current.map((reward, j) => j === i ? { ...reward, ...patch } : reward));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!rewards.length || iapIssues.length || !submitting.current.begin()) return;
    setResult("");
    mutation.mutate(captureMail(target, rewards, subject, body));
  }

  return <AccountActionDialog title="Send mail" scope="Stored in our backend inbox; legacy-client delivery is not confirmed." target={target} pending={mutation.isPending} changed={changed} onClose={onClose}>
    <form onSubmit={submit} className="space-y-4">
      <fieldset disabled={mutation.isPending} className="space-y-4">
      <label className="block space-y-1 text-sm">Subject
        <Input autoFocus value={subject} onChange={(e) => { setSubject(e.target.value); setChanged(true); setResult(""); }} />
      </label>
      <label className="block space-y-1 text-sm">Body (optional)
        <textarea value={body} onChange={(e) => { setBody(e.target.value); setChanged(true); setResult(""); }} rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
      </label>
      <div className="space-y-3">
        <div className="text-sm font-medium">Rewards</div>
        {rewards.map((reward, i) => {
          const fields = rewardFields[reward.rewardType] ?? {};
          return <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
            <select aria-label={`Reward ${i + 1} type`} value={reward.rewardType} onChange={(e) => update(i, {
              rewardType: Number(e.target.value), amount: 1, itemID: undefined, hero: undefined,
              developerBuffID: undefined, recipe: undefined, iapProductId: undefined,
            })} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
              {Object.entries(REWARD_TYPE_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            {fields.amount && <Input aria-label={`Reward ${i + 1} amount`} type="number" value={reward.amount}
              onChange={(e) => update(i, { amount: parseInt(e.target.value) || 0 })} className="w-28" />}
            {fields.iap && <IapPicker value={reward.iapProductId ?? ""} onChange={(iapProductId) => update(i, { iapProductId })} />}
            {fields.item && <ItemPicker className="min-w-48 flex-1" value={reward.itemID ?? ""} onChange={(id) => update(i, { itemID: id })} />}
            {fields.hero && <select aria-label={`Reward ${i + 1} hero`} value={reward.hero ?? ""}
              onChange={(e) => update(i, { hero: Number(e.target.value) })} className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="" disabled>Pick a hero</option>
              {Object.entries(HERO_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>}
            {fields.buff && <Input aria-label={`Reward ${i + 1} buff ID`} value={reward.developerBuffID ?? ""}
              onChange={(e) => update(i, { developerBuffID: e.target.value })} placeholder="Buff ID" className="flex-1" />}
            {fields.recipe && <Input aria-label={`Reward ${i + 1} recipe ID`} type="number" value={reward.recipe ?? ""}
              onChange={(e) => update(i, { recipe: parseInt(e.target.value) || 0 })} className="w-28" />}
            <Button type="button" variant="destructive" size="sm" aria-label={`Remove reward ${i + 1}`}
              onClick={() => { setRewards(rewards.filter((_, j) => j !== i)); setChanged(true); setResult(""); }}>✕</Button>
          </div>;
        })}
        <Button type="button" variant="secondary" size="sm" onClick={() => {
          setRewards([...rewards, { rewardType: 2, amount: 0 }]); setChanged(true); setResult("");
        }}>+ Add Reward</Button>
      </div>
      {iapIssues.length > 0 && <Alert variant="destructive"><AlertDescription>
        <p>Resolve or remove these attachments before sending:</p>
        <ul className="list-disc pl-5">{iapIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
      </AlertDescription></Alert>}
      {mutation.error && <Alert variant="destructive"><AlertDescription>{String(mutation.error)}. The request may have reached the server; check mail history before retrying.</AlertDescription></Alert>}
      {result && <Alert><AlertDescription>{result}</AlertDescription></Alert>}
      <Button type="submit" disabled={!rewards.length || iapIssues.length > 0 || mutation.isPending}>{mutation.isPending ? "Sending…" : "Send mail"}</Button>
      </fieldset>
    </form>
  </AccountActionDialog>;
}
